import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';

import MyAccountPartnerDetails from '@/components/myaccount/Applicant_MyAccount/MyAccountPartnerDetails';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';

import AccountSettings from './AccountSettings';
import PrimaryContactSection from './PrimaryContactSection';

import AccountShippingContact from './AccountShippingContact';
import SideNav from '@/components/myaccount/Applicant_MyAccount/SideNav';
import TabPanel from '@/components/common/TabPanel';
import TabSubPanel from '@/components/myaccount/Applicant_MyAccount/TabSubPanel';
import { ProgramNames } from '@/models/Program';

export default function Applicant_MyAccount() {
  const { selectedProgramId, selectedProgramInfo } = useContext(SelectedProgramContext);
  const { partnerInfo } = useContext(SelectedPartnerContext);
  const { translatedText } = useContext(LocaleContext);

  const [value, setValue] = useState(0);
  const [subValue, setSubValue] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const showShippingAddress = selectedProgramId !== ProgramNames.TreeCitiesWorld;

  const pendingTabRef = useRef<number | null>(null);
  const pendingSubTabRef = useRef<number | null>(null);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (hasUnsavedChanges) {
      pendingTabRef.current = newValue;
      setShowWarning(true);
      return;
    }
    setValue(newValue);
    if (newValue !== 1) {
      setSubValue(0);
    }
  };

  const handleSubChange = (event: React.SyntheticEvent, newValue: number) => {
    if (hasUnsavedChanges) {
      pendingSubTabRef.current = newValue;
      setShowWarning(true);
      return;
    }
    setSubValue(newValue);
  };

  const handleDiscardChanges = () => {
    setHasUnsavedChanges(false);
    setShowWarning(false);
    if (pendingTabRef.current !== null) {
      setValue(pendingTabRef.current);
      if (pendingTabRef.current !== 1) {
        setSubValue(0);
      }
      pendingTabRef.current = null;
    }
    if (pendingSubTabRef.current !== null) {
      setSubValue(pendingSubTabRef.current);
      pendingSubTabRef.current = null;
    }
  };

  const handleKeepEditing = () => {
    setShowWarning(false);
    pendingTabRef.current = null;
    pendingSubTabRef.current = null;
  };

  useEffect(() => {
    if (!showShippingAddress && subValue === 2) {
      setSubValue(0);
    }
  }, [showShippingAddress, subValue]);

  return (
    <>
      <Dialog open={showWarning} onClose={handleKeepEditing}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          You have unsaved changes. Do you want to discard them and navigate away?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleKeepEditing} variant='primary'>
            Keep Editing
          </Button>
          <Button onClick={handleDiscardChanges} variant='secondary'>
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ flexGrow: 1, display: 'flex', height: '760px' }} width='100%'>
        <SideNav
          value={value}
          onChange={handleChange}
          subValue={subValue}
          onSubChange={handleSubChange}
          partnerTerm={
            selectedProgramInfo?.partnerTerm != null ? selectedProgramInfo?.partnerTerm : ''
          }
          partnerName={partnerInfo?.partnerName != null ? partnerInfo?.partnerName : ''}
          showShippingAddress={showShippingAddress}
        />

        <TabPanel value={value} index={0} headerText={translatedText.myaccount.accountSettings}>
          <AccountSettings
            onUnsavedChanges={() => setHasUnsavedChanges(true)}
            onSave={() => setHasUnsavedChanges(false)}
          />
        </TabPanel>

        <TabPanel
          value={value}
          index={1}
          headerText={translatedText.myaccount.partnerDetails(
            partnerInfo?.partnerName != null ? partnerInfo?.partnerName : ''
          )}
        >
          <TabSubPanel value={subValue} index={0}>
            <PrimaryContactSection
              onUnsavedChanges={() => setHasUnsavedChanges(true)}
              onSave={() => setHasUnsavedChanges(false)}
            />
          </TabSubPanel>

          <TabSubPanel value={subValue} index={1}>
            <MyAccountPartnerDetails
              onUnsavedChanges={() => setHasUnsavedChanges(true)}
              onSave={() => setHasUnsavedChanges(false)}
            />
          </TabSubPanel>

          {showShippingAddress && (
            <TabSubPanel value={subValue} index={2}>
              <AccountShippingContact
                programId={selectedProgramId}
                onUnsavedChanges={() => setHasUnsavedChanges(true)}
                onSave={() => setHasUnsavedChanges(false)}
              />
            </TabSubPanel>
          )}
        </TabPanel>
      </Box>
    </>
  );
}
